import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { analytics } from 'core/analytics';

import DocumentHead from 'legacyComponents/documentHead';
import { Link } from 'core/routing';
import FormActionButton from 'libs/forms/views/formActionButton';
import { Center, Indicator } from 'legacyComponents/styled/lib';
import { Dot } from 'apps/components/shared';

import Button from 'components/Button';
import Tooltip from 'components/Tooltip';
import { ButtonGroup } from 'legacyComponents/styles';

import { imageSrc } from 'apps/file/utils';
import { clientStore } from 'apps/client/app';
import { Follow } from 'apps/content/views/follow';
import { conf as mainConf } from 'apps/config.yaml';

import { PhaseBadge } from 'apps/project/views/phaseBadge';
import { ProjectDescription } from 'apps/project/views/projectDescription';
import { ProjectImage } from 'apps/project/views/projectImage';
import { License } from 'apps/project/views/license';
import FilesProportion from 'apps/project/views/filesProportion';
import {
  ProjectInfoTerm,
  ProjectInfoDef,
  ProjectInfo,
  ParagraphStyled,
  Overview,
  NameIndicator,
  PhaseIndicator,
  ImageIndicator,
  DataIndicatorStyled,
  Wrapper,
  InfoWrapper,
  RowWrapper,
  ContributorRowWrapper,
  LinkContributor,
  Contributor,
  ContributorDiff,
  DateStyled,
  ArrowIcon,
  CTALink,
} from 'apps/project/views/overview.styles';
import { withNamespaces, Trans } from 'react-i18next';

export const DescriptionIndicator = () => (
  <>
    <Indicator width='100%' margin='20px 0 10px 0' />
    <Indicator width='90%' margin='0 0 10px 0' />
    <Indicator width='80%' margin='0 0 10px 0' />
  </>
);

export const DataIndicator = () => (
  <DataIndicatorStyled>
    <Indicator margin='0 0 15px 0' />
    <Indicator margin='0 0 15px 0' />
    <Indicator margin='0 0 15px 0' />
    <Indicator margin='0 0 15px 0' />
    <Indicator margin='0 0 15px 0' />
    <Indicator margin='0 0 15px 0' />
    <Indicator margin='0 0 15px 0' />
    <Indicator margin='0 0 15px 0' />
  </DataIndicatorStyled>
);

export const ProjectOverviewIndicator = () => (
  <Overview atHead>
    <Wrapper>
      <ImageIndicator />
      <InfoWrapper>
        <NameIndicator />
        <PhaseIndicator />
        <DescriptionIndicator />
        <DataIndicator />
      </InfoWrapper>
    </Wrapper>
  </Overview>
);

const CONTRIBUTORS_VISIBLE = 5;
@withNamespaces()
@observer
export class ProjectOverview extends React.Component {
  render() {
    const { projectManager, repoManager, t } = this.props;
    const { project, pendingInvitation } = projectManager;

    if (project === null) return null;

    const {
      editing,
      contributions,
      canUpdate,
      Edit,
      AcceptInvitation,
      RejectInvitation,
    } = project;

    const formStore = editing ? project.editFormStore : null;

    const { contributorsConn } = project.manager;

    const contributors = contributorsConn?.arrayApp || [];

    let contributorList = [];

    // FIXME - replace with forEach (because the for loop has an if)
    // eslint-disable-next-line
    for (const [index, contributor] of contributors.entries()) {
      if (index < CONTRIBUTORS_VISIBLE) {
        contributorList.push(
          <LinkContributor
            name='profile'
            params={{ profile: contributor.username }}
            key={contributor.id}
            content={contributor.content}
          >
            <Contributor
              src={imageSrc(
                contributor.avatar,
                contributor.imageFallbackPath,
                mainConf.imgSizes.profile.small,
              )}
              only={index === 0 && contributorsConn.totalCount === 1}
            />
          </LinkContributor>,
        );
      }
    }
    if (contributorsConn?.totalCount > CONTRIBUTORS_VISIBLE) {
      contributorList.push(
        <ContributorDiff onClick={projectManager.ShowMoreContributors}>
          +{contributorsConn.totalCount - CONTRIBUTORS_VISIBLE}
        </ContributorDiff>,
      );
    }
    const spaceType = project.space.spaceType.toLowerCase();
    if (contributorList.length === 0) {
      if (project.canUpdate) {
        contributorList = (
          <CTALink
            name={`${spaceType}.project.settings.teams`}
            params={{
              [`${spaceType}`]: project.spacePath,
              slug: project.slug,
            }}
          >
            Invite collaborators
            <ArrowIcon name='arrow_right' size='small' />
          </CTALink>
        );
      } else {
        contributorList = null;
      }
    }

    const bottomButtons = pendingInvitation ? (
      <ButtonGroup>
        <Button
          onClick={() => AcceptInvitation(pendingInvitation)}
          text={<Trans>Accept Invite</Trans>}
        />
        <Button
          variant='destructive'
          onClick={() => RejectInvitation(pendingInvitation)}
          text={<Trans>Reject Invite</Trans>}
        />
      </ButtonGroup>
    ) : project.canUpdate ? null : (
      <Follow content={project.content} size='default' />
    );

    return (
      <Overview atHead={repoManager.atHead}>
        <DocumentHead title={`${project.name} | Overview`} />
        <Wrapper>
          <ProjectImage app={project} formStore={formStore} fieldName='image' />
          <InfoWrapper data-intercom-target='project-description'>
            <ProjectDescription
              app={project}
              formStore={formStore}
              fieldName='description'
              placeholder={t('Give your Project a description')}
            />

            <RowWrapper>
              <ContributorRowWrapper>{contributorList}</ContributorRowWrapper>
              <PhaseBadge project={project} />
            </RowWrapper>
            <ProjectInfo>
              <ProjectInfoTerm>
                <Trans>Last activity</Trans>
              </ProjectInfoTerm>
              <ProjectInfoDef>
                <DateStyled value={project.lastUpdated} short />
                <Dot variant='success'>●</Dot>
              </ProjectInfoDef>
              <ProjectInfoTerm>
                <Trans>Contributions</Trans>
              </ProjectInfoTerm>
              <ProjectInfoDef>
                {project.canUpdate && contributions.totalCount === 0 ? (
                  <CTALink
                    name={`${spaceType}.project.files`}
                    params={{
                      [`${spaceType}`]: project.spacePath,
                      slug: project.slug,
                    }}
                  >
                    <Trans>Upload product files</Trans>
                    <ArrowIcon name='arrow_right' size='small' />
                  </CTALink>
                ) : (
                  contributions.totalCount
                )}
              </ProjectInfoDef>
              {!project.private && (
                <>
                  <ProjectInfoTerm>
                    <Trans>Views</Trans>
                  </ProjectInfoTerm>
                  <ProjectInfoDef>{project.content.pageViews}</ProjectInfoDef>
                </>
              )}
              <License app={project} repoManager={repoManager} />
              {repoManager.routerStore.path.split('/')[1] === '+opennext' && (
                <FilesProportion app={project} />
              )}
            </ProjectInfo>
          </InfoWrapper>
        </Wrapper>
        <Center>
          {bottomButtons}
          {canUpdate || clientStore.isStaff ? (
            editing ? (
              <Center>
                <FormActionButton
                  formStore={formStore}
                  buttonHandler={project.Cancel}
                  buttonText='Cancel'
                  variant='tertiary'
                  marginRight='6px'
                  marginTop='0px'
                  outline
                />
                <FormActionButton
                  formStore={formStore}
                  buttonHandler={projectManager.Save}
                  buttonText='Save'
                  buttonTextPosting='Saving...'
                  marginLeft='6px'
                  marginTop='0px'
                />
              </Center>
            ) : !['s', 'xs'].includes(clientStore.activeBreakPoint) ? (
              <Center>
                <Tooltip id='tooltipSettings' place='right' delayShow={1000} />
                {canUpdate && (
                  <Button
                    text={<Trans>Edit</Trans>}
                    style={{ marginRight: '10px' }}
                    onClick={() => Edit()}
                  />
                )}
                <Link
                  onClick={() => {
                    analytics.sendEvent(
                      'clicked-settings-project',
                      {},
                      { ga: true },
                    );
                  }}
                  name={`${clientStore.spaceRoute.route}.settings`}
                  params={clientStore.spaceParams}
                  content={project.content}
                >
                  <Button
                    icon='settings'
                    ariaLabel='Settings'
                    data-tip={t('Project settings')}
                    style={{ marginLeft: '10px' }}
                    data-for='tooltipSettings'
                    outline
                  />
                </Link>
              </Center>
            ) : (
              <ParagraphStyled size='small'>
                (
                <Trans>
                  <strong>Note</strong>: Making edits and contributions is
                  currently not supported from mobile devices
                </Trans>
                )
              </ParagraphStyled>
            )
          ) : null}
        </Center>
      </Overview>
    );
  }
}

ProjectOverview.propTypes = {
  projectManager: PropTypes.object,
  repoManager: PropTypes.object,
  loading: PropTypes.bool,
  t: PropTypes.func,
};
